import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useToast } from '../../../components/ui/Toast';

const PostDinnerFeedback = ({ eventData, selectedGuests, onSave, onClose }) => {
  const { showSuccess } = useToast();
  const [feedbackData, setFeedbackData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock guest data with enhanced information
  const mockGuests = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      avatar: "https://images.unsplash.com/photo-1730222168387-051038de25be",
      avatarAlt: "Professional woman with shoulder-length brown hair smiling at camera",
      winePreferences: "Prefers white wine, especially Sauvignon Blanc",
      foodPreferences: "Loves Mediterranean cuisine, enjoys fresh herbs and olive oil dishes"
    },
    {
      id: 2,
      name: "Michael Johnson", 
      email: "michael.johnson@email.com",
      avatar: "https://images.unsplash.com/photo-1705645930353-0e335311ef20",
      avatarAlt: "Professional man with short dark hair in blue shirt smiling",
      winePreferences: "Enjoys red wine, particularly Cabernet Sauvignon",
      foodPreferences: "Big fan of grilled meats and hearty American dishes"
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      email: "emma.rodriguez@email.com", 
      avatar: "https://images.unsplash.com/photo-1597725096039-7a277e984835",
      avatarAlt: "Young Hispanic woman with long dark hair and bright smile",
      winePreferences: "Prefers organic wines, sometimes brings homemade agua frescas",
      foodPreferences: "Passionate about plant-based Mexican cuisine and Asian fusion"
    }
  ];

  const getSelectedGuestData = () => {
    return mockGuests?.filter(guest => selectedGuests?.includes(guest?.id));
  };

  const handleFeedbackChange = (guestId, field, value) => {
    setFeedbackData(prev => ({
      ...prev,
      [guestId]: {
        ...prev?.[guestId],
        [field]: value
      }
    }));
  };

  const handleSubmitFeedback = async () => {
    setIsSubmitting(true);
    
    try {
      // Simulate API call to save feedback
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update guest preferences in the system
      const updatedFeedback = Object.entries(feedbackData)?.map(([guestId, feedback]) => ({
        guestId: parseInt(guestId),
        eventId: eventData?.id || Date.now(),
        eventTitle: eventData?.title,
        eventDate: eventData?.date,
        wineComments: feedback?.wineComments || '',
        foodComments: feedback?.foodComments || '',
        overallRating: feedback?.overallRating || 5,
        additionalNotes: feedback?.additionalNotes || '',
        submittedAt: new Date()?.toISOString()
      }));

      onSave?.(updatedFeedback);
      showSuccess('Guest feedback saved successfully!');
      onClose?.();
      
    } catch (error) {
      console.error('Error saving feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedGuestData = getSelectedGuestData();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-warm-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Post-Dinner Feedback</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Collect wine and food preferences from your guests
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Event Information */}
          <div className="bg-muted/30 p-4 rounded-lg">
            <h3 className="font-medium text-foreground mb-2">Event Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Event:</span>
                <span className="ml-2 text-foreground font-medium">{eventData?.title || 'Dinner Event'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Date:</span>
                <span className="ml-2 text-foreground font-medium">
                  {eventData?.date ? new Date(eventData?.date)?.toLocaleDateString() : 'Not set'}
                </span>
              </div>
            </div>
          </div>

          {/* Guest Feedback Forms */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="MessageSquare" size={20} />
              <span>Guest Preferences & Feedback</span>
            </h3>

            {selectedGuestData?.map((guest) => (
              <div key={guest?.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start space-x-4 mb-4">
                  <Image
                    src={guest?.avatar}
                    alt={guest?.avatarAlt}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{guest?.name}</h4>
                    <p className="text-sm text-muted-foreground">{guest?.email}</p>
                    
                    {/* Current Preferences Display */}
                    <div className="mt-2 space-y-1">
                      {guest?.winePreferences && (
                        <p className="text-xs text-muted-foreground">
                          <Icon name="Wine" size={12} className="inline mr-1" />
                          Current wine preference: {guest?.winePreferences}
                        </p>
                      )}
                      {guest?.foodPreferences && (
                        <p className="text-xs text-muted-foreground">
                          <Icon name="Utensils" size={12} className="inline mr-1" />
                          Current food preference: {guest?.foodPreferences}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Wine Comments */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      <Icon name="Wine" size={14} className="inline mr-1" />
                      Wine Comments & Preferences
                    </label>
                    <textarea
                      value={feedbackData?.[guest?.id]?.wineComments || ''}
                      onChange={(e) => handleFeedbackChange(guest?.id, 'wineComments', e?.target?.value)}
                      placeholder="What wines did they enjoy? Any new preferences discovered?"
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Food Comments */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      <Icon name="Utensils" size={14} className="inline mr-1" />
                      Food Comments & Preferences
                    </label>
                    <textarea
                      value={feedbackData?.[guest?.id]?.foodComments || ''}
                      onChange={(e) => handleFeedbackChange(guest?.id, 'foodComments', e?.target?.value)}
                      placeholder="Which dishes did they love? Any new food preferences noted?"
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {/* Overall Rating */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Overall Experience Rating
                    </label>
                    <select
                      value={feedbackData?.[guest?.id]?.overallRating || 5}
                      onChange={(e) => handleFeedbackChange(guest?.id, 'overallRating', parseInt(e?.target?.value))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    >
                      <option value={5}>⭐⭐⭐⭐⭐ Excellent</option>
                      <option value={4}>⭐⭐⭐⭐ Good</option>
                      <option value={3}>⭐⭐⭐ Average</option>
                      <option value={2}>⭐⭐ Below Average</option>
                      <option value={1}>⭐ Poor</option>
                    </select>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Additional Notes
                    </label>
                    <Input
                      type="text"
                      value={feedbackData?.[guest?.id]?.additionalNotes || ''}
                      onChange={(e) => handleFeedbackChange(guest?.id, 'additionalNotes', e?.target?.value)}
                      placeholder="Any other observations or notes..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Tips */}
          <div className="bg-info/5 border border-info/20 rounded-lg p-4">
            <h4 className="font-medium text-info-foreground mb-2 flex items-center space-x-2">
              <Icon name="Lightbulb" size={16} />
              <span>Tips for Better Future Events</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-info-foreground">
              <div className="space-y-1">
                <p>🍷 <strong>Wine Pairings:</strong> Note which wines worked best with your menu</p>
                <p>🍽️ <strong>Food Preferences:</strong> Track evolving tastes and new dietary needs</p>
              </div>
              <div className="space-y-1">
                <p>💬 <strong>Conversation:</strong> What topics or activities were most engaging?</p>
                <p>📝 <strong>Planning:</strong> Use this feedback for future guest selections and menus</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleSubmitFeedback}
              loading={isSubmitting}
              iconName="Save"
              iconPosition="left"
            >
              Save Feedback
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDinnerFeedback;